import React from 'react'
import CardUI from '../../../widgets/layout/CardUI'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import BadgeUI from '../../../widgets/output/BadgeUI'
import SpinnerUI from '../../../widgets/output/SpinnerUI'
import './Project.css'

const ProjectUI = ({ context, loan, project, onRefreshClick, loading, errorList, projectFileList, onDownloadFileClick, onDownloadCustomerFileClick }) => {
  const formatCurrency = (amount) => {
    if (!amount) return 'N/A'
    if (typeof amount === 'string' && amount.startsWith('₹')) {
      return amount // Return as is if it's already formatted
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getStatusBadge = (value, type = 'default') => {
    if (value === 'Yes' || value === true) {
      return <BadgeUI text="✓ Available" type="success" />;
    } else if (value === 'No' || value === false) {
      return <BadgeUI text="✗ Not Available" type="error" />;
    }
    return <span className='g_value'>{value}</span>;
  };

  const getProjectTypeBadge = (projectType) => {
    if (projectType === 'solar_ppa') {
      return <BadgeUI text="Solar PPA" type="primary" />;
    }
    return <BadgeUI text={projectType} type="secondary" />;
  };

  // Resolve file lists from new API shape (fallback to legacy prop if present)
  const resolvedProjectFileList = (project && project.projectFileList) ? project.projectFileList : projectFileList;
  const resolvedCustomerFileList = (project && project.customerFileList) ? project.customerFileList : [];

  return (
    <div className='su_prj'>
      <LRTableActionsUI 
        location='top'
        left={
          <span className='g_flex_left g_flex_h'>
            <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} />
            <span className='g_m_left_100 g_large'>
              <b>Project Details</b>
            </span>
          </span>
        }
      />
      
      {loading && (
        <div className='g_flex_h g_flex_top g_m_top_50'>
          <div className='project-info-card'>
            <CardUI title='Project Information'>
              <div className='g_flex_v g_flex_center' style={{minHeight: '200px'}}>
                <SpinnerUI />
                <div className='g_m_top_20'>
                  <span className='g_text'>Loading project information...</span>
                </div>
              </div>
            </CardUI>
          </div>
        </div>
      )}
      
      {!loading && !project && errorList && errorList.length > 0 && (
        <div className='g_flex_h g_flex_top g_m_top_50'>
          <div className='project-info-card'>
            <CardUI title='Project Information'>
              <div className='g_flex_v g_flex_center' style={{minHeight: '200px'}}>
                <div className='g_error_text g_text_center'>
                  <i className='icon-alert-triangle' style={{fontSize: '48px', display: 'block', marginBottom: '16px'}} />
                  <div style={{fontSize: '18px', marginBottom: '8px'}}>Project Not Found</div>
                  <div>{errorList[0] && typeof errorList[0] === 'object' ? errorList[0].message : String(errorList[0])}</div>
                </div>
              </div>
            </CardUI>
          </div>
        </div>
      )}

      {!loading && project && (
        <div className='g_flex_h g_flex_top g_m_top_50 project-info-row'>
          <div className='project-info-card'>
            <CardUI title='Project Information'>
              <div className='g_flex_v'>
                {project.projectType === 'solar_ppa' ? (
                  <>
                    <div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Name:</span>
                        <span className='g_value project-name'>{project.projectName}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Type:</span>
                        <span className='g_value'>{project.projectType}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Offtaker/Counterparty PAN:</span>
                        <span className='g_value'>{project.offtakerCounterpartyPan}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Solar Project Capacity (KWs):</span>
                        <span className='g_value'>{project.solarProjectCapacity ? `${project.solarProjectCapacity} KW` : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Tariff (per KWh):</span>
                        <span className='g_value'>{project.tariff || 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Location:</span>
                        <span className='g_value'>{project.location || 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Tenure of PPA (years):</span>
                        <span className='g_value'>{project.tenure ? `${project.tenure} years` : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Lock-in Period (years):</span>
                        <span className='g_value'>{project.lockInPeriod ? `${project.lockInPeriod} years` : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Expected PLF (%):</span>
                        <span className='g_value'>{project.expectedPlf ? `${project.expectedPlf}%` : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Cost:</span>
                        <span className='g_value project-cost'>{project.projectCost || 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Commission Date:</span>
                        <span className='g_value'>{project.commissioningDate ? new Date(project.commissioningDate).toLocaleDateString() : 'N/A'}</span>
                      </div>
                    </div>
                  </>
                ) : project.projectType === 'po' ? (
                  <>
                    <div className='project-section'>
                      <h4 className='section-title'>Basic Information</h4>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Name:</span>
                        <span className='g_value project-name'>{project.projectName}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Type:</span>
                        <span className='g_value'>{project.projectType}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>CounterParty/OfftakerPan:</span>
                        <span className='g_value'>{project.offtakerCounterpartyPan}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Order Value:</span>
                        <span className='g_value'>{project.orderValue ? project.orderValue.toLocaleString() : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Cost:</span>
                        <span className='g_value'>{project.projectCost ? project.projectCost.toLocaleString() : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>PO Expiry Date:</span>
                        <span className='g_value'>
                          {project.poExpiryDate
                            ? new Date(
                                project.poExpiryDate > 1e12
                                  ? project.poExpiryDate
                                  : project.poExpiryDate * 1000
                              ).toLocaleDateString()
                            : 'N/A'}
                        </span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Expected Margin:</span>
                        <span className='g_value'>{project.expectedMarginPercent ? `${project.expectedMarginPercent}%` : 'N/A'}</span>
                      </div>
                    </div>
                  </>
                ) : project.projectType === 'logistics' ? (
                  <>
                    <div className='project-section'>
                      <h4 className='section-title'>Basic Information</h4>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Name:</span>
                        <span className='g_value project-name'>{project.projectName}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Type:</span>
                        <span className='g_value'>{project.projectType}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Cost:</span>
                        <span className='g_value'>{project.projectCost || 'N/A'}</span>
                      </div>
                    </div>

                    <div className='project-section g_m_top_20'>
                      <h4 className='section-title'>Logistics Contracts</h4>
                      {project.logisticsContractList && project.logisticsContractList.map((contract, index) => (
                        <div key={contract.contractId} className='g_m_bottom_20'>
                          <h5 className='g_m_bottom_10'>Contract {index + 1}</h5>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Counterparty PAN:</span>
                            <span className='g_value'>{contract.counterpartyPan && contract.counterpartyPan.trim() || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Contract Tenure:</span>
                            <span className='g_value'>{contract.contractTenure ? `${contract.contractTenure} months` : 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Number of Vehicles:</span>
                            <span className='g_value'>{contract.numberOfVehicles || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Vehicle Make:</span>
                            <span className='g_value'>{contract.vehicleMake || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Vehicle Model:</span>
                            <span className='g_value'>{contract.vehicleModel || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Vehicle Cost:</span>
                            <span className='g_value'>{contract.vehicleCost || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Revenue Per Month Per Vehicle:</span>
                            <span className='g_value'>{contract.revenuePerMonthPerVehicle || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Direct Cost Per Month Per Vehicle:</span>
                            <span className='g_value'>{contract.directCostPerMonthPerVehicle || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Total Cost:</span>
                            <span className='g_value'>{contract.totalCost || 'N/A'}</span>
                          </div>
                        </div>
                      ))}
                      {(!project.logisticsContractList || project.logisticsContractList.length === 0) && (
                        <div className='g_text'>No logistics contracts available.</div>
                      )}
                    </div>
                  </>
                ) : project.projectType === 'rental_subscriptions' ? (
                  <>
                    <div className='project-section'>
                      <h4 className='section-title'>Basic Information</h4>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Name:</span>
                        <span className='g_value project-name'>{project.projectName}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Type:</span>
                        <span className='g_value'>{project.projectType}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Number of Vehicles:</span>
                        <span className='g_value'>{project.numberOfVehicles || 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Vehicle Make:</span>
                        <span className='g_value'>{project.vehicleMake || 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Vehicle Model:</span>
                        <span className='g_value'>{project.vehicleModel || 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Vehicle Cost:</span>
                        <span className='g_value'>{project.vehicleCost ? formatCurrency(project.vehicleCost) : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Cost:</span>
                        <span className='g_value'>{project.projectCost ? formatCurrency(project.projectCost) : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Revenue Per Month:</span>
                        <span className='g_value'>{project.revenuePerMonth ? formatCurrency(project.revenuePerMonth) : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Direct Cost Per Month:</span>
                        <span className='g_value'>{project.directCostPerMonth ? formatCurrency(project.directCostPerMonth) : 'N/A'}</span>
                      </div>
                    </div>
                  </>
                ) : project.projectType === 'asset_management' ? (
                  <>
                    <div className='project-section'>
                      <h4 className='section-title'>Basic Information</h4>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Name:</span>
                        <span className='g_value project-name'>{project.projectName}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Type:</span>
                        <span className='g_value'>{project.projectType}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Cost:</span>
                        <span className='g_value'>{project.projectCost ? formatCurrency(project.projectCost) : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Counterparty PAN:</span>
                        <span className='g_value'>{project.offtakerCounterpartyPan || 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Tenure of Contract:</span>
                        <span className='g_value'>{project.contractTenure ? `${project.contractTenure} months` : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Revenue Per Month:</span>
                        <span className='g_value'>{project.revenuePerMonth ? formatCurrency(project.revenuePerMonth) : 'N/A'}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Direct Cost Per Month:</span>
                        <span className='g_value'>{project.directCostPerMonth ? formatCurrency(project.directCostPerMonth) : 'N/A'}</span>
                      </div>
                    </div>

                    <div className='project-section g_m_top_20'>
                      <h4 className='section-title'>Asset List</h4>
                      {project.assetList && project.assetList.map((asset, index) => (
                        <div key={asset.assetId} className='g_m_bottom_20'>
                          <h5 className='g_m_bottom_10'>Asset {index + 1}</h5>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Asset Type:</span>
                            <span className='g_value'>{asset.assetType || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Number of Assets:</span>
                            <span className='g_value'>{asset.numberOfAssets || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Asset Make:</span>
                            <span className='g_value'>{asset.assetMake || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Asset Model:</span>
                            <span className='g_value'>{asset.assetModel || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Asset Cost:</span>
                            <span className='g_value'>{asset.assetCost ? formatCurrency(asset.assetCost) : 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Total Cost:</span>
                            <span className='g_value'>{asset.totalCost ? formatCurrency(asset.totalCost) : 'N/A'}</span>
                          </div>
                        </div>
                      ))}
                      {(!project.assetList || project.assetList.length === 0) && (
                        <div className='g_text'>No assets available.</div>
                      )}
                    </div>
                  </>
                ) : project.projectType === 'cash_flow' ? (
                  <>
                    <div className='project-section'>
                      <h4 className='section-title'>Basic Information</h4>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Name:</span>
                        <span className='g_value project-name'>{project.projectName}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Type:</span>
                        <span className='g_value'>{project.projectType}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Cost:</span>
                        <span className='g_value'>{project.projectCost ? formatCurrency(project.projectCost) : 'N/A'}</span>
                      </div>
                    </div>

                    <div className='project-section g_m_top_20'>
                      <h4 className='section-title'>Cash Flow Details</h4>
                      {project.cashFlowList && project.cashFlowList.map((cashFlow, index) => (
                        <div key={cashFlow.cashFlowId} className='g_m_bottom_20'>
                          <h5 className='g_m_bottom_10'>Cash Flow {index + 1}</h5>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Material Name:</span>
                            <span className='g_value'>{cashFlow.materialName || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Supplier Name:</span>
                            <span className='g_value'>{cashFlow.supplierName || 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Total Cost:</span>
                            <span className='g_value'>{cashFlow.totalCost ? formatCurrency(cashFlow.totalCost) : 'N/A'}</span>
                          </div>
                          <div className='g_flex_h g_flex_between project-info-row-item'>
                            <span className='g_label'>Counterparty PAN:</span>
                            <span className='g_value'>{cashFlow.counterpartyPan || 'N/A'}</span>
                          </div>
                        </div>
                      ))}
                      {(!project.cashFlowList || project.cashFlowList.length === 0) && (
                        <div className='g_text'>No cash flow details available.</div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className='project-section'>
                      <h4 className='section-title'>Basic Information</h4>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Name:</span>
                        <span className='g_value project-name'>{project.projectName}</span>
                      </div>
                      <div className='g_flex_h g_flex_between project-info-row-item'>
                        <span className='g_label'>Project Type:</span>
                        <span className='g_value'>{project.projectType}</span>
                      </div>
                      {project.projectCost && (
                        <div className='g_flex_h g_flex_between project-info-row-item'>
                          <span className='g_label'>Project Cost:</span>
                          <span className='g_value'>{formatCurrency(project.projectCost)}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CardUI>
          </div>
          {/* Project Files Section */}
          <div className='project-files-card'>
            <CardUI title='Project Files'>
              <div className='project-files-list'>
                {resolvedProjectFileList && resolvedProjectFileList.length > 0 ? (
                  resolvedProjectFileList.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName;
                    return (
                      <div key={idx} className='project-file-item'>
                        <a
                          className='project-file-link'
                          href='#'
                          onClick={e => {
                            e.preventDefault();
                            onDownloadFileClick(fileName);
                          }}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {fileName}
                        </a>
                      </div>
                    );
                  })
                ) : (
                  <span className='g_text'>No files available.</span>
                )}
              </div>
            </CardUI>
          </div>

          {/* Customer Files Section */}
          <div className='project-files-card'>
            <CardUI title='Customer Details'>
              <div className='project-files-list'>
                <div className='g_flex_h g_flex_between project-info-row-item'>
                  <span className='g_label'>Payment Modes:</span>
                  <span className='g_value'>
                    {project && Array.isArray(project.paymentModes) && project.paymentModes.length > 0
                      ? project.paymentModes.join(', ')
                      : 'N/A'}
                  </span>
                </div>
                <div className='g_flex_h g_flex_between project-info-row-item'>
                  <span className='g_label'>Customer PAN(s):</span>
                  <span className='g_value'>
                    {project && Array.isArray(project.customerPanList) && project.customerPanList.length > 0
                      ? project.customerPanList.join(', ')
                      : 'N/A'}
                  </span>
                </div>
                <h4 className='section-title g_m_top_20'>Customer invoice(s) / PO(s)</h4>
                {resolvedCustomerFileList && resolvedCustomerFileList.length > 0 ? (
                  resolvedCustomerFileList.map((file, idx) => {
                    const fileName = typeof file === 'string' ? file : file.fileName;
                    return (
                      <div key={idx} className='project-file-item'>
                        <a
                          className='project-file-link'
                          href='#'
                          onClick={e => {
                            e.preventDefault();
                            if (onDownloadCustomerFileClick) {
                              onDownloadCustomerFileClick(fileName);
                            } else {
                              onDownloadFileClick(fileName);
                            }
                          }}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {fileName}
                        </a>
                      </div>
                    );
                  })
                ) : (
                  <span className='g_text'>No customer files available.</span>
                )}
              </div>
            </CardUI>
          </div>
        </div>
      )}
      {/* Error List Section */}
      {errorList && errorList.length > 0 && !(!loading && !project) && (
        <div className='g_m_top_20'>
          {errorList.map((err, idx) => (
            <div key={idx} className='g_error_text'>
              {typeof err === 'object' ? err.message : String(err)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
export default ProjectUI;
